import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';
import EmptyTable from 'Components/EmptyTable';
import UISpinner from 'Components/UIComponents/UISpinner';
import useIsMobile from 'Hooks/Common/useIsMobile';
import { LtdTypes, PlanTypes } from 'Interfaces/Billing';
import { SelectableItem, TablePaginationProps } from 'Interfaces/Common';
import { AuthorizedRoutePaths, settingsRedirectionMap } from 'Interfaces/RoutePaths';
import { TeamMember } from 'Interfaces/Team';
import { User, UserRoles } from 'Interfaces/User';
import { selectTeamLimitAndLtdType } from 'Utils/selectors';

import TeamMemberItem from './TeamMemberItem';
import { TeamMemberListMobileView } from './TeamMemberListMobileView';

import IconPause from 'Assets/images/icons/pause.svg';
import IconPlay from 'Assets/images/icons/play.svg';
import IconRemove from 'Assets/images/icons/remove-icon.svg';
import IconSort from 'Assets/images/icons/sort.svg';
import TeamIcon from 'Assets/images/icons/team-empty-icon.svg';

interface TeamMembersListProps {
  user: User;
  teamMembers: SelectableItem<TeamMember>[];
  paginationProps?: TablePaginationProps;
  toggleItemSelection?: (teamMemberId: string) => void;
  isLoading: boolean;
  requestOrdering?: (key: keyof TeamMember) => void;
  openDeleteModal: () => void;
  openFreezeModal: () => void;
  openUnfreezeModal: () => void;
  isDeleteModalOpen: boolean;
  isFreezeModalOpen: boolean;
  isUnfreezeModalOpen: boolean;
}

function TeamMembersList({
  teamMembers,
  toggleItemSelection,
  isLoading,
  requestOrdering,
  openDeleteModal,
  openFreezeModal,
  openUnfreezeModal,
  isDeleteModalOpen,
  isFreezeModalOpen,
  isUnfreezeModalOpen,
  user,
}: TeamMembersListProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [_, ltdType] = useSelector(selectTeamLimitAndLtdType);

  const selectedTeamMembers = teamMembers.filter(teamMember => teamMember.isSelected);

  const [isFrozenSelected, isActiveSelected] = useMemo(
    () => [
      selectedTeamMembers.some(teamMember => teamMember.frozenUntil),
      selectedTeamMembers.some(teamMember => !teamMember.frozenUntil),
    ],
    [selectedTeamMembers],
  );

  const isBusinessPlan = useMemo(() => {
    return !!user.plan.type && user.plan.type === PlanTypes.BUSINESS;
  }, [user.plan.type]);

  const isTableEmpty = useMemo(() => {
    return (!isBusinessPlan && !user.teamId) || teamMembers.length === 0;
  }, [isBusinessPlan, user.teamId, teamMembers.length]);

  const isOwnerOrAdmin = useMemo(() => {
    return user.role === UserRoles.OWNER || user.role === UserRoles.ADMIN;
  }, [user.role]);

  const emptyTableProps = useMemo(() => {
    return !isBusinessPlan
      ? {
          buttonText: 'Upgrade to Business',
          headerText: 'Start adding team members!',
          description: 'Upgrade to business to add team members to your account',
        }
      : {
          buttonClassName: 'team__button--hide',
          headerText: 'Start adding team members!',
        };
  }, [isBusinessPlan]);

  if (isTableEmpty)
    return (
      <div className="documents__empty-table">
        <EmptyTable
          {...emptyTableProps}
          onClick={() => {
            navigate(settingsRedirectionMap[AuthorizedRoutePaths.SETTINGS_BILLING_PLAN]);
          }}
          icon={TeamIcon}
          iconClassName="empty-table__icon--team"
        />
      </div>
    );

  return isMobile ? (
    <TeamMemberListMobileView
      isLoading={false}
      teamMembers={teamMembers}
      isEditEnabled={isOwnerOrAdmin}
      isChangingRoleEnabled={user.role === UserRoles.OWNER}
      toggleItemSelection={toggleItemSelection}
      user={user}
      isOwnerOrAdmin={isOwnerOrAdmin}
      openDeleteModal={openDeleteModal}
      openFreezeModal={openFreezeModal}
      openUnfreezeModal={openUnfreezeModal}
    />
  ) : (
    <div className="table documents__table">
      <div
        className={classNames(
          'table__tableControls tableControls__controlsGroup team__controlsGroup',
        )}
      >
        {isOwnerOrAdmin && (
          <>
            {ltdType === LtdTypes.NONE && (
              <>
                <button
                  onClick={openFreezeModal}
                  className="tableControls__control tableControls__control--blue"
                  disabled={selectedTeamMembers.length === 0 || isFrozenSelected}
                >
                  <ReactSVG src={IconPause} className="tableControls__control-icon" />
                  Freeze
                </button>
                <button
                  onClick={openUnfreezeModal}
                  className="tableControls__control tableControls__control--blue"
                  disabled={selectedTeamMembers.length === 0 || isActiveSelected}
                >
                  <ReactSVG src={IconPlay} className="tableControls__control-icon" />
                  Unfreeze
                </button>
              </>
            )}
            <button
              onClick={openDeleteModal}
              className="tableControls__control tableControls__control--red"
            >
              <ReactSVG src={IconRemove} className="tableControls__control-icon" />
              Delete
            </button>
          </>
        )}
      </div>
      <div className="table__container">
        <div className="table__innerContainer">
          <div className="table__row table__header">
            <div className="table__column table__column--check" />
            <div className="table__column table__column--text team__column-member">
              <button
                className="tableControls__headerControl"
                onClick={() => requestOrdering && requestOrdering('name')}
              >
                <span>MEMBER</span>
                <ReactSVG src={IconSort} />
              </button>
            </div>
            <div className="table__column table__column--team-email">
              <button
                className="tableControls__headerControl"
                onClick={() => requestOrdering && requestOrdering('email')}
              >
                <span>EMAIL</span>
                <ReactSVG src={IconSort} />
              </button>
            </div>
            <div className="table__column table__column--team-role">
              <button className="tableControls__headerControl">
                <span>STATUS</span>
              </button>
            </div>
            <div className="table__column table__column--team-role">
              <button
                className="tableControls__headerControl"
                onClick={() => requestOrdering && requestOrdering('role')}
              >
                <span>ROLE</span>
                <ReactSVG src={IconSort} />
              </button>
            </div>
            {isOwnerOrAdmin && (
              <div className="table__column table__column--action team__column-actions">
                ACTIONS
              </div>
            )}
          </div>
          {isLoading ? (
            <div className="documents__spinner">
              <UISpinner width={50} height={50} className="spinner--main__wrapper" />
            </div>
          ) : (
            teamMembers.map(teamMember => {
              return (
                <TeamMemberItem
                  key={teamMember.id}
                  teamMember={teamMember}
                  toggleSelect={() =>
                    toggleItemSelection && toggleItemSelection(teamMember.id)
                  }
                  isSelected={teamMember.isSelected}
                  isSelectable={
                    teamMember.id !== user.id &&
                    teamMember.role !== UserRoles.OWNER &&
                    (user.role === UserRoles.OWNER ||
                      (teamMember.role === UserRoles.USER && isOwnerOrAdmin))
                  }
                  isEditEnabled={isOwnerOrAdmin}
                  isChangingRoleEnabled={user.role === UserRoles.OWNER}
                  className={classNames({
                    'table__dataRow--delete': isDeleteModalOpen && teamMember.isSelected,
                    'table__dataRow--inactive':
                      (isDeleteModalOpen || isFreezeModalOpen || isUnfreezeModalOpen) &&
                      !teamMember.isSelected,
                    'table__dataRow--frozen': teamMember.frozenUntil,
                  })}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default TeamMembersList;
